import { useEffect, useRef, useState } from "react";
import { Search, X, FileText } from "lucide-react";
import { api, type SearchHit } from "../lib/api.js";
import { Input } from "./ui/forms.js";

interface SearchBarProps {}

export function SearchBar({}: SearchBarProps) {
  const [q, setQ] = useState("");
  const [hits, setHits] = useState<SearchHit[]>([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!q.trim()) {
      setHits([]);
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      setBusy(true);
      try {
        const { hits } = await api.search(q.trim(), { limit: 8 });
        if (!cancelled) {
          setHits(hits);
          setOpen(true);
        }
      } finally {
        if (!cancelled) setBusy(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [q]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener("mousedown", onClick);
    return () => window.removeEventListener("mousedown", onClick);
  }, []);

  return (
    <div ref={ref} className="relative">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onFocus={() => hits.length > 0 && setOpen(true)}
            placeholder="Search synced OneNote pages and attachments..."
            className="pl-9 pr-9"
          />
          {q && (
            <button
              onClick={() => {
                setQ("");
                setHits([]);
                setOpen(false);
              }}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-slate-400 hover:bg-slate-100"
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </div>
        {busy && <span className="text-xs text-slate-400">searching...</span>}
      </div>

      {open && hits.length > 0 && (
        <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-md border border-slate-200 bg-white shadow-lg">
          <ul className="max-h-96 overflow-y-auto">
            {hits.map((h) => (
              <li
                key={h.pageId}
                className="border-b border-slate-100 px-3 py-2 last:border-b-0 hover:bg-slate-50"
              >
                <div className="flex items-start gap-2">
                  <FileText className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-sm font-medium text-slate-900">
                        {h.title}
                      </span>
                      <span className="shrink-0 text-xs text-slate-400">
                        {h.notebook} / {h.section}
                      </span>
                    </div>
                    <div
                      className="mt-0.5 line-clamp-2 text-xs text-slate-600"
                      dangerouslySetInnerHTML={{ __html: h.snippet }}
                    />
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {open && q.trim() && !busy && hits.length === 0 && (
        <div className="absolute z-20 mt-1 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-xs text-slate-500 shadow-lg">
          No matches in synced corpus. Try syncing more notebooks/sections, or
          ask the agent to search live OneNote.
        </div>
      )}
    </div>
  );
}
